/* eslint-disable no-param-reassign */
import { eventChannel, delay } from 'redux-saga'
import { all, take, call, put, race, select, takeEvery } from 'redux-saga/effects'

import { Messages } from '../Socket'
import { SocketCreators } from '../../actions'
// import { Types } from './actions'

import config from '../Config'

/**
 * Saga eventChannel factory to handle socket events
 * @param {object} WebSocket instance from the watcher
 */

let socketInterval

export function watchMessages(socket, onSocketOpenAction) {
  return eventChannel((emitter) => {
    // Close websocket if still trying to connect after X seconds
    const forceCloseTimeout = setTimeout(() => {
      if (socket.readyState === 0) {
        socket.close(4001)
      }
    }, config.webSocketTimeout)

    /* Socket Opened */
    socket.onopen = () => {
      clearTimeout(forceCloseTimeout)
      emitter(SocketCreators.socketConnectSuccess())
      emitter(onSocketOpenAction)

      socketInterval = setInterval(() => {
        emitter(SocketCreators.sendPing())
      }, config.pingTimer)
    }

    /* Socket Message Received */
    socket.onmessage = (response) => {
      const result = JSON.parse(response.data)
      try {
        if (result.type) {
          const method = config.snakeToCamel(result.type)
          emitter(SocketCreators[method](result))
        }
      } catch (exception) {
        emitter(SocketCreators.socketErrorNotification(exception))
      }
    }

    /* Socket Closed */
    socket.onclose = (evt) => {
      clearInterval(socketInterval)
      emitter(SocketCreators.handleWebSocketClose(evt))
    }

    /* Socket Error */
    socket.onerror = (evt) => {
      emitter(SocketCreators.socketConnectFailure(evt))
    }

    /* eventChannel factory must return a function for when the channel closes */
    return () => {
      clearInterval(socketInterval)
      socket.close(1000)
    }
  })
}

/**
 * Generator to handle closing of the WebSocket
 * Attempts automatic reconnects if abnormal close e.g.
 *    - socket closed having previously connected & not explicity asked to close
 *    - evt code is anything but 1000 (normal close) or 1005 (no status received)
 */

export function* handleWebSocketClose(evt) {
  const {
    Socket: { previouslyConnected, isReconnecting },
  } = yield select()
  const validStatusCodes = [1000, 1005, 4000]
  if (!previouslyConnected || validStatusCodes.indexOf(evt.code) > -1) {
    // If customer is banned
    if (evt.code === 4000) {
      yield put(SocketCreators.banUser())
      yield put(SocketCreators.stopWebSocket({ reconnect: true }))
    } else {
      /* expected closure - don't reconnect */
      yield put(SocketCreators.stopWebSocket({ reconnect: false }))
    }
  } else {
    /* abnormal close - reconnect */
    if (!isReconnecting) yield put(SocketCreators.setIsReconnecting(true))
    yield put(SocketCreators.stopWebSocket({ reconnect: true }))
  }
}

export function* sendPing() {
  const {
    Chat: { chatId },
    Socket: { pingResponse },
  } = yield select()
  if (!pingResponse) {
    clearInterval(socketInterval)
    yield put(SocketCreators.setPingResponseError(true))
    return
  }
  yield put(SocketCreators.setPingResponse(false))
  yield put(SocketCreators.sendSocketMessage(Messages.ping({ chatId })))
}

/**
 * Generator to listen for internal action dispatches
 * for sending socket messages
 * @param {object} socket instance
 */

export function* internalListener(socket) {
  while (true) {
    const action = yield take('SEND_SOCKET_MESSAGE')
    // Don't send socket message unless NEW_CHAT_REQUEST or there is a chatId
    if (action.message && (action.message.type === 'NEW_CHAT_REQUEST' || action.message.chatId)) {
      socket.send(JSON.stringify(action.message))
    }
  }
}

/**
 * Generator to listen for external action dispatches
 * from saga eventChannel
 * @param {object} eventChannel
 */

export function* externalListener(socketChannel) {
  while (true) {
    const action = yield take(socketChannel)
    if (action.type === 'HANDLE_WEB_SOCKET_CLOSE') {
      yield call(handleWebSocketClose, action.event)
    }

    yield put(action)
  }
}

export function* startWebSocket({ brand, department, webSocketUrl }) {
  try {
    yield put(SocketCreators.socketConnectAttempt())

    const {
      Chat: { chatId, authenticationKey },
      Socket: { userIsBanned },
    } = yield select()

    const socketUrl = webSocketUrl || config.getWebSocketUrl()
    const url = userIsBanned && chatId ? `${socketUrl}?chatId=${chatId}` : socketUrl
    const socket = new WebSocket(url)

    const isRenewChat = !!(chatId && authenticationKey)

    const onSocketOpenAction = isRenewChat
      ? SocketCreators.sendRenewChatRequest(chatId, authenticationKey)
      : SocketCreators.sendNewChatRequest(brand, department)

    const socketChannel = yield call(watchMessages, socket, onSocketOpenAction)

    /* bi-directional websocket listeners */
    const { cancel } = yield race({
      task: [call(externalListener, socketChannel), call(internalListener, socket)],
      cancel: take('STOP_WEB_SOCKET'),
    })

    if (cancel) {
      socketChannel.close()
      const {
        options: { reconnect },
      } = cancel

      if (reconnect) {
        yield put(SocketCreators.reconnectWebSocket(brand, department, webSocketUrl))
      }
    }
  } catch (exception) {
    console.info(exception)
  }
}

/**
 * Generator to handle reconnection of web socket
 * Increments number of attempts each iteration until limit is hit
 */

export function* reconnectWebSocket({ brand, department, webSocketUrl }) {
  const {
    Socket: { reconnectAttempts },
  } = yield select()

  yield delay(config.reconnectionTimeout)

  if (reconnectAttempts <= config.maxReconnectAttempts) {
    yield put(SocketCreators.startWebSocket(brand, department, webSocketUrl))
  } else {
    yield put(SocketCreators.setIsReconnecting(false))
    yield put(SocketCreators.setPingResponseError(false))
    yield put(SocketCreators.socketConnectFailure(config.maxReconnectError))
  }
}

/**
 * Watcher
 */
export default function* rootSaga() {
  yield all([
    takeEvery('START_WEB_SOCKET', startWebSocket),
    takeEvery('RECONNECT_WEB_SOCKET', reconnectWebSocket),
    takeEvery('SEND_PING', sendPing),
  ])
}
